import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { updateTimeLeft, updateTeamSessionState } from '../store/slices/teamFocusSessionSlice';
import { TeamSessionState } from '../store/slices/teamFocusSessionSlice';

/**
 * Keeps the countdown for a team focus session running and
 * dispatches updates to the Redux store.
 *
 * The timer starts when `session.sessionStarted` becomes true
 * and the session is neither paused nor completed. It ticks once
 * per second, decrementing `sessionTimeLeft` and marking the
 * session complete when the counter hits zero.
 *
 * All of the values that the effect depends on are destructured
 * so that callers of the hook do not need to pass the entire
 * session object as a dependency in their own effects.
 */
export function useTeamSessionTimer(teamId: string | undefined, session: TeamSessionState | null) {
    const dispatch = useDispatch();
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const {
        sessionStarted,
        sessionPaused,
        sessionCompleted,
        sessionTimeLeft,
    } = session || {} as Partial<TeamSessionState>;

    useEffect(() => {
        if (teamId && sessionStarted && !sessionPaused && !sessionCompleted) {
            timerRef.current = setInterval(() => {
                if (sessionTimeLeft && sessionTimeLeft > 0) {
                    dispatch(updateTimeLeft({ teamId, timeLeft: sessionTimeLeft - 1 }));
                } else if (teamId) {
                    dispatch(
                        updateTeamSessionState({
                            teamId,
                            updates: { sessionCompleted: true, sessionStarted: false },
                        })
                    );
                    if (timerRef.current) clearInterval(timerRef.current);
                }
            }, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [teamId, sessionStarted, sessionPaused, sessionCompleted, sessionTimeLeft, dispatch]);
}
